import Modal from 'react-modal';
import "../../styles/AdminPage.css";
import Button, { BUTTON_TYPES } from '../Button';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import DateInput from '../inputs/DateInput';
import { FaTimes } from "react-icons/fa";
import CheckboxGroupScrollable from '../inputs/CheckboxGroupScrollable';
import { useAdmin } from '../../pages/admin/AdminContext';
import LabeledCheckbox from '../inputs/LabeledCheckBox';
import { DateTimeFormatter, LocalDateTime } from '@js-joda/core';
import toast from 'react-hot-toast';

Modal.setAppElement('#root');

const EventFilterModal = ({ isOpen, onClose, initialFormData, onFormDataChange, onApply, semesters }) => {
    const [isArchiveEnabled, setIsArchiveEnabled] = useState(() =>
        initialFormData?.isArchiveEnabled || false
    );
    const { committees, eventTypes } = useAdmin();

    const eventTypesFinal = React.useMemo(() => [
        ...eventTypes.map((eventTypeSingle) => ({
            id: eventTypeSingle.id,
            eventType: eventTypeSingle.eventType
        })),
        { id: eventTypes.length + 1, eventType: 'Incomplete Event' },
    ], [eventTypes]);

    const committeeTypes = React.useMemo(() => [
        { id: 0, committeeType: 'None' },
        ...committees.map((committee) => ({
            id: committee.id,
            committeeType: committee.committeeName
        }))
    ], [committees]);

    const recurrenceTypes = useMemo(() => [
        { id: 1, recurrenceType: 'No Recurrence' },
        { id: 2, recurrenceType: 'Daily' },
        { id: 3, recurrenceType: 'Weekly' },
        { id: 4, recurrenceType: 'Monthly' },
    ], []);

    const [selectedEventTypeIDs, setSelectedEventTypeIDs] = useState(() =>
        initialFormData?.selectedEventTypeIDs
            ? initialFormData.selectedEventTypeIDs.map(id => Number(id))
            : []
    );
    const [selectedSemesterTypeIDs, setSelectedSemesterTypeIDs] = useState(() =>
        initialFormData?.selectedSemesterTypeIDs
            ? initialFormData.selectedSemesterTypeIDs.map(id => Number(id))
            : []
    );
    const [selectedCommitteeTypeIDs, setSelectedCommitteeTypeIDs] = useState(() =>
        initialFormData?.selectedCommitteeTypeIDs
            ? initialFormData.selectedCommitteeTypeIDs.map(id => Number(id))
            : []
    );
    const [selectedRecurrenceTypeIDs, setSelectedRecurrenceTypeIDs] = useState(() =>
        initialFormData?.selectedRecurrenceTypeIDs
            ? initialFormData.selectedRecurrenceTypeIDs.map(id => Number(id))
            : []
    );
    const [filterStartDate, setFilterStartDate] = useState(initialFormData?.filterStartDate || "");
    const [filterEndDate, setFilterEndDate] = useState(initialFormData?.filterEndDate || "");

    const computedDefaultFilters = useMemo(() => ({
        selectedEventTypeIDs: [],
        selectedSemesterTypeIDs: [],
        selectedCommitteeTypeIDs: [],
        selectedRecurrenceTypeIDs: [],
        filterStartDate: "",
        filterEndDate: "",
        isArchiveEnabled: false
    }), []);

    const selectedEventTypeNames = useMemo(() => {
        return selectedEventTypeIDs
            .map(id => eventTypesFinal.find(et => et.id === id)?.eventType)
            .filter(Boolean);
    }, [selectedEventTypeIDs, eventTypesFinal]);

    const selectedSemesterTypeNames = useMemo(() => {
        return selectedSemesterTypeIDs
            .map(id => semesters.find(st => st.id === id)?.semesterTitle)
            .filter(Boolean);
    }, [selectedSemesterTypeIDs, semesters]);

    const selectedCommitteeTypeNames = useMemo(() => {
        return selectedCommitteeTypeIDs
            .map(id => committeeTypes.find(ct => ct.id === id)?.committeeType)
            .filter(Boolean);
    }, [selectedCommitteeTypeIDs, committeeTypes]);

    const selectedRecurrenceTypeNames = useMemo(() => {
        return selectedRecurrenceTypeIDs
            .map(id => recurrenceTypes.find(rt => rt.id === id)?.recurrenceType)
            .filter(Boolean);
    }, [selectedRecurrenceTypeIDs, recurrenceTypes]);

    const didInit = useRef(false);

    const onFormDataChangeRef = useRef(onFormDataChange);
    useEffect(() => {
        onFormDataChangeRef.current = onFormDataChange;
    }, [onFormDataChange]);

    useEffect(() => {
        if (isOpen && !didInit.current) {
            setSelectedEventTypeIDs(
                initialFormData?.selectedEventTypeIDs
                    ? initialFormData.selectedEventTypeIDs.map(id => Number(id))
                    : []
            );
            setSelectedSemesterTypeIDs(
                initialFormData?.selectedSemesterTypeIDs
                    ? initialFormData.selectedSemesterTypeIDs.map(id => Number(id))
                    : []
            );
            setSelectedCommitteeTypeIDs(
                initialFormData?.selectedCommitteeTypeIDs
                    ? initialFormData.selectedCommitteeTypeIDs.map(id => Number(id))
                    : []
            );
            setSelectedRecurrenceTypeIDs(
                initialFormData?.selectedRecurrenceTypeIDs
                    ? initialFormData.selectedRecurrenceTypeIDs.map(id => Number(id))
                    : []
            );
            setFilterStartDate(initialFormData?.filterStartDate || "");
            setFilterEndDate(initialFormData?.filterEndDate || "");
            setIsArchiveEnabled(initialFormData?.isArchiveEnabled || false);
            didInit.current = true;
        } else if (!isOpen) {
            didInit.current = false;
        }
    }, [isOpen, initialFormData, eventTypesFinal, semesters, committeeTypes, recurrenceTypes]);

    useEffect(() => {
        if (onFormDataChangeRef.current) {
            onFormDataChangeRef.current({
                selectedEventTypeIDs,
                selectedEventTypeNames,
                selectedSemesterTypeIDs,
                selectedSemesterTypeNames,
                selectedCommitteeTypeIDs,
                selectedCommitteeTypeNames,
                selectedRecurrenceTypeIDs,
                selectedRecurrenceTypeNames,
                filterStartDate,
                filterEndDate,
                isArchiveEnabled,
                defaultFilters: computedDefaultFilters
            });
        }
    }, [selectedEventTypeIDs, selectedEventTypeNames,
        selectedSemesterTypeIDs, selectedSemesterTypeNames,
        selectedCommitteeTypeIDs, selectedCommitteeTypeNames,
        selectedRecurrenceTypeIDs, selectedRecurrenceTypeNames,
        filterStartDate, filterEndDate, isArchiveEnabled, computedDefaultFilters]);

    const handleEventTypeSelect = (evt) => {
        const changedValue = Number(evt.target.value);
        const isChecked = evt.target.checked;
        if (isChecked) {
            setSelectedEventTypeIDs(prev => [...prev, changedValue]);
        } else {
            setSelectedEventTypeIDs(prev => prev.filter(id => id !== changedValue));
        }
    };

    useEffect(() => {
        requestAnimationFrame(() => {
            const checkboxes = document.querySelectorAll('input[name="eventTypesGroup"]');
            checkboxes.forEach((checkbox) => {
                const value = Number(checkbox.value);
                checkbox.checked = selectedEventTypeIDs.includes(value);
            });
        });
    }, [selectedEventTypeIDs]);

    const handleSemesterTypeSelect = (evt) => {
        const changedValue = Number(evt.target.value);
        const isChecked = evt.target.checked;
        if (isChecked) {
            setSelectedSemesterTypeIDs(prev => [...prev, changedValue]);
        } else {
            setSelectedSemesterTypeIDs(prev => prev.filter(id => id !== changedValue));
        }
    };

    useEffect(() => {
        requestAnimationFrame(() => {
            const checkboxes = document.querySelectorAll('input[name="semesterTypesGroup"]');
            checkboxes.forEach((checkbox) => {
                const value = Number(checkbox.value);
                checkbox.checked = selectedSemesterTypeIDs.includes(value);
            });
        });
    }, [selectedSemesterTypeIDs]);

    const handleCommitteeTypeSelect = (evt) => {
        const changedValue = Number(evt.target.value);
        const isChecked = evt.target.checked;
        if (isChecked) {
            setSelectedCommitteeTypeIDs(prev => [...prev, changedValue]);
        } else {
            setSelectedCommitteeTypeIDs(prev => prev.filter(id => id !== changedValue));
        }
    };

    useEffect(() => {
        requestAnimationFrame(() => {
            const checkboxes = document.querySelectorAll('input[name="committeeTypesGroup"]');
            checkboxes.forEach((checkbox) => {
                const value = Number(checkbox.value);
                checkbox.checked = selectedCommitteeTypeIDs.includes(value);
            });
        });
    }, [selectedCommitteeTypeIDs]);

    const handleRecurrenceTypeSelect = (evt) => {
        const changedValue = Number(evt.target.value);
        const isChecked = evt.target.checked;
        if (isChecked) {
            setSelectedRecurrenceTypeIDs(prev => [...prev, changedValue]);
        } else {
            setSelectedRecurrenceTypeIDs(prev => prev.filter(id => id !== changedValue));
        }
    };

    useEffect(() => {
        requestAnimationFrame(() => {
            const checkboxes = document.querySelectorAll('input[name="recurrenceTypesGroup"]');
            checkboxes.forEach((checkbox) => {
                const value = Number(checkbox.value);
                checkbox.checked = selectedRecurrenceTypeIDs.includes(value);
            });
        });
    }, [selectedRecurrenceTypeIDs]);

    const handleReset = () => {
        setSelectedEventTypeIDs([]);
        setSelectedSemesterTypeIDs([]);
        setSelectedCommitteeTypeIDs([]);
        setSelectedRecurrenceTypeIDs([]);
        setFilterStartDate("");
        setFilterEndDate("");
        setIsArchiveEnabled(false);
    };

    const handleApply = () => {
        if (filterStartDate && filterEndDate) {
            const isCorrectDate = LocalDateTime.parse(filterStartDate, DateTimeFormatter.ISO_DATE_TIME)
                .isBefore(LocalDateTime.parse(filterEndDate, DateTimeFormatter.ISO_DATE_TIME));

            if (!isCorrectDate) {
                toast.error("Start Date & Time must be\n before End Date & Time.");
                return;
            }
        }

        if (onApply) {
            onApply({
                selectedEventTypeIDs,
                selectedEventTypeNames,
                selectedSemesterTypeIDs,
                selectedSemesterTypeNames,
                selectedCommitteeTypeIDs,
                selectedCommitteeTypeNames,
                selectedRecurrenceTypeIDs,
                selectedRecurrenceTypeNames,
                filterStartDate,
                filterEndDate,
                isArchiveEnabled
            });
        }
        onClose();
    };

    const handleArchiveChange = (event) => {
        setIsArchiveEnabled(event.target.checked);
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            contentLabel="Event Filter Modal"
            className="custom-modal-content"
            overlayClassName="custom-modal-overlay"
        >
            <>
                <div className='d-flex flex-row justify-content-between align-items-center mb-3'>
                    <h2 className="text-start mb-0">Filter Events</h2>
                    <div className='d-flex flex-row justify-content-end align-items-center me-1'>
                        <div className='me-4'>
                            <LabeledCheckbox
                                id="archive-checkbox"
                                label="Include Archived Events?: "
                                checked={isArchiveEnabled}
                                onChange={handleArchiveChange}
                                disabled={false}
                            />
                        </div>
                        <button className="close-btn" style={{ marginTop: '-4px' }} onClick={onClose}>
                            <FaTimes />
                        </button>
                    </div>
                </div>
                <div className="d-flex flex-row justify-content-between mb-1">
                    <div className="form-field w-50 me-2">
                        <DateInput
                            id="eventStartDateFilter"
                            className="form-input"
                            label='Start Date'
                            value={filterStartDate}
                            setValue={(e) => setFilterStartDate(e.target.value)}
                        />
                    </div>
                    <div className="form-field w-50">
                        <DateInput
                            id="eventEndDateFilter"
                            className="form-input"
                            label='End Date'
                            value={filterEndDate}
                            setValue={(e) => setFilterEndDate(e.target.value)}
                        />
                    </div>
                </div>
                <div className="d-flex flex-row justify-content-between mb-1">
                    <div className="form-field w-50 me-2">
                        <CheckboxGroupScrollable
                            id="event-types"
                            name="eventTypesGroup"
                            label='Event Type(s)'
                            options={eventTypesFinal.map(et => ({
                                value: et.id,
                                label: et.eventType,
                            }))}
                            selectedValues={selectedEventTypeIDs}
                            onChange={handleEventTypeSelect}
                            onSelectAll={() => setSelectedEventTypeIDs(eventTypesFinal.map(et => et.id))}
                            onUnselectAll={() => setSelectedEventTypeIDs([])}
                            medium={true}
                        />
                    </div>
                    <div className="form-field w-50 me-2">
                        <CheckboxGroupScrollable
                            id="semester-types"
                            name="semesterTypesGroup"
                            label='Semester(s)'
                            options={semesters.map(st => ({
                                value: st.id,
                                label: st.semesterTitle,
                            }))}
                            selectedValues={selectedSemesterTypeIDs}
                            onChange={handleSemesterTypeSelect}
                            onSelectAll={() => setSelectedSemesterTypeIDs(semesters.map(st => st.id))}
                            onUnselectAll={() => setSelectedSemesterTypeIDs([])}
                            medium={true}
                        />
                    </div>
                </div>
                <div className="d-flex flex-row justify-content-between mb-1">
                    <div className="form-field w-50 me-2">
                        <CheckboxGroupScrollable
                            id="committee-types"
                            name="committeeTypesGroup"
                            label='Committee(s)'
                            options={committeeTypes.map(ct => ({
                                value: ct.id,
                                label: ct.committeeType,
                            }))}
                            selectedValues={selectedCommitteeTypeIDs}
                            onChange={handleCommitteeTypeSelect}
                            onSelectAll={() => setSelectedCommitteeTypeIDs(committeeTypes.map(ct => ct.id))}
                            onUnselectAll={() => setSelectedCommitteeTypeIDs([])}
                            medium={true}
                        />
                    </div>
                    <div className="form-field w-50">
                        <CheckboxGroupScrollable
                            id="recurrence-types"
                            name="recurrenceTypesGroup"
                            label='Recurrence'
                            options={recurrenceTypes.map(rt => ({
                                value: rt.id,
                                label: rt.recurrenceType,
                            }))}
                            selectedValues={selectedRecurrenceTypeIDs}
                            onChange={handleRecurrenceTypeSelect}
                            onSelectAll={() => setSelectedRecurrenceTypeIDs(recurrenceTypes.map(rt => rt.id))}
                            onUnselectAll={() => setSelectedRecurrenceTypeIDs([])}
                            medium={true}
                        />
                    </div>
                </div>
                <div className='d-flex flex-row justify-content-center align-items-center two-button-container'>
                    <div className='me-3'>
                        <Button
                            id="reset-button"
                            type={BUTTON_TYPES.outline}
                            buttonText="Reset"
                            action={handleReset}
                        />
                    </div>
                    <Button
                        id="filter-button-modal"
                        type={BUTTON_TYPES.primary}
                        buttonText="Apply"
                        action={handleApply}
                    />
                </div>
            </>
        </Modal>
    );
};

EventFilterModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    initialFormData: PropTypes.object,
    onFormDataChange: PropTypes.func,
    onApply: PropTypes.func,
    semesters: PropTypes.array,
};

export default EventFilterModal;
